import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { SerieHeader } from '@/components/masterclass/SerieHeader';
import { SeriePlayer } from '@/components/masterclass/SeriePlayer';
import { SerieDescription } from '@/components/masterclass/SerieDescription';
import { SerieSidebar } from '@/components/masterclass/SerieSidebar';
import { SerieActions } from '@/components/masterclass/SerieActions';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useMasterClassProgress } from '@/hooks/useMasterClassProgress';
import { useMasterClassKeyboard } from '@/hooks/useMasterClassKeyboard';
import type { Serie, Aula } from '@/types/masterclass';
import seriesData from '@/data/masterclass/series.json';
import lessonsData from '@/data/masterclass/lessons.json';
import { cn } from '@/lib/utils';
import { Menu } from 'lucide-react';

export default function MasterClassSerie() {
  useLanguage();
  const { serieId, aulaId } = useParams<{ serieId: string; aulaId?: string }>();
  const navigate = useNavigate();
  const playerRef = useRef<HTMLDivElement>(null);

  const [serie, setSerie] = useState<Serie | null>(null);
  const [aulas, setAulas] = useState<Aula[]>([]);
  const [currentAulaId, setCurrentAulaId] = useState<string | null>(aulaId || null);
  const [isTheater, setIsTheater] = useState(() => {
    if (serieId) {
      const saved = localStorage.getItem(`mc_theater_${serieId}`);
      return saved === 'true';
    }
    return false;
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { progressoPct, aulas: progressAulas, updateProgress } = useMasterClassProgress(serieId || '');

  // Carregar série e aulas
  useEffect(() => {
    if (!serieId) return;

    const foundSerie = seriesData.find((s) => s.id === serieId) as Serie;
    if (foundSerie) {
      setSerie(foundSerie);
      const serieAulas = lessonsData.filter((l) => l.serieId === serieId) as Aula[];
      setAulas(serieAulas);
    }

    console.log('[Telemetry] mc_series_view', { serieId });
  }, [serieId]);

  // Auto-carregar primeira aula se não houver aulaId na URL
  useEffect(() => {
    if (!aulaId && aulas.length > 0 && serieId) {
      const proximaNaoConcluida = aulas.find(
        (a) => progressAulas[a.id]?.status !== 'concluida'
      );
      const firstAulaId = proximaNaoConcluida?.id || aulas[0].id;
      navigate(`/master-class/serie/${serieId}/aula/${firstAulaId}`, { replace: true });
    }
  }, [aulaId, aulas, serieId, progressAulas, navigate]);

  // Detectar mudança de aula via URL
  useEffect(() => {
    if (aulaId) {
      setCurrentAulaId(aulaId);
      console.log('[Telemetry] mc_lesson_view', { serieId, aulaId });
      
      // Scroll suave para o player
      setTimeout(() => {
        playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [aulaId, serieId]);

  // Persistir preferência de modo teatro
  useEffect(() => {
    if (serieId) {
      localStorage.setItem(`mc_theater_${serieId}`, String(isTheater));
      console.log('[Telemetry] mc_toggle_theater', { serieId, theaterOn: isTheater });
    }
  }, [isTheater, serieId]);

  // Keyboard shortcuts
  useMasterClassKeyboard({
    onClose: () => navigate('/master-class')
  });

  if (!serieId || !serie) {
    return <Navigate to="/master-class" replace />;
  }

  if (aulas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p className="text-muted-foreground mb-4">
          {translateInterfaceText("Nenhuma aula publicada nesta série ainda.")}</p>
        <Button onClick={() => navigate('/master-class')}>{translateInterfaceText("Voltar")}</Button>
      </div>
    );
  }

  const currentAula = aulas.find((a) => a.id === currentAulaId);
  const currentIndex = aulas.findIndex((a) => a.id === currentAulaId);
  const nextAula = currentIndex >= 0 && currentIndex < aulas.length - 1 ? aulas[currentIndex + 1] : null;

  const handleSelectAula = (id: string) => {
    setDrawerOpen(false);
    navigate(`/master-class/serie/${serieId}/aula/${id}`);
  };

  const handleMarkComplete = () => {
    if (currentAulaId) {
      updateProgress(currentAulaId, 0, 'concluida', aulas.length);
      console.log('[Telemetry] mc_mark_complete', { serieId, aulaId: currentAulaId });
    }
  };

  const handleNextLesson = () => {
    if (nextAula) {
      navigate(`/master-class/serie/${serieId}/aula/${nextAula.id}`);
    }
  };

  const handleTimeUpdate = (time: number) => {
    if (currentAulaId) {
      updateProgress(currentAulaId, time, 'em_andamento', aulas.length);
    }
  };

  const handleVideoEnded = () => {
    handleMarkComplete();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <SerieHeader
        serie={serie}
        progressoPct={progressoPct}
        onBack={() => navigate('/master-class')}
      />

      {/* Mobile: Botão Aulas */}
      <div className="lg:hidden fixed top-16 right-4 z-40">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDrawerOpen(true)}
          className="gap-2"
        >
          <Menu className="h-4 w-4" />
          {translateInterfaceText("Aulas (")}{aulas.length})
        </Button>
      </div>

      {/* Layout Principal */}
      <div className={cn("page-lesson", isTheater && "page-lesson--theater")}>
        {/* Sidebar - Desktop */}
        <div className="page-lesson__sidebar hidden lg:block">
          <SerieSidebar
            aulas={aulas}
            currentAulaId={currentAulaId || ''}
            serieId={serieId}
            onSelect={handleSelectAula}
          />
        </div>

        {/* Conteúdo Principal */}
        <div className="page-lesson__content">
          <div ref={playerRef}>
            <SeriePlayer
              aula={currentAula || null}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnded}
              isTheater={isTheater}
              onTheaterToggle={() => setIsTheater(!isTheater)}
            />
          </div>

          <div className="page-lesson__desc">
            <SerieDescription aula={currentAula || null} />
          </div>

          <div className="mt-6">
            <SerieActions
              currentAula={currentAula || null}
              nextAula={nextAula}
              onMarkComplete={handleMarkComplete}
              onNextLesson={handleNextLesson}
              progressAulas={progressAulas}
            />
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[300px] sm:w-[400px]">
          <div className="mt-6">
            <SerieSidebar
              aulas={aulas}
              currentAulaId={currentAulaId || ''}
              serieId={serieId}
              onSelect={handleSelectAula}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
